'use client';

/**
 * ApiClientError + SERVER_ERROR_MESSAGES for Profileia API client.
 * Verbatim extraction from frontend/src/lib/api-client.ts:
 *   - ApiClientError: L1358-1384
 *   - SERVER_ERROR_MESSAGES: L1970-2008
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 */

class ApiClientError extends Error {
  constructor(
    message: string,
    public code: string,
    public status: number,
    public details?: unknown,
  ) {
    super(message);
  }

  /**
   * BUG-FE-016: returns true if the error is transient and the user can
   * reasonably retry the same request (5xx server errors, network
   * failures, timeouts). 4xx errors are NOT retryable (client-side
   * problem), except 408 (Request Timeout — server-side/network).
   *
   * BUG-EXP5-003: added 408 (TIMEOUT) to the retryable set — a hung
   * request that triggered our internal AbortController should be safe
   * to retry (the backend likely recovered or the network condition
   * cleared).
   */
  isRetryable(): boolean {
    return this.status === 0 || this.status === 408 || this.status >= 500;
  }
}

export { ApiClientError };

const SERVER_ERROR_MESSAGES: Record<number, string> = {
  500: 'Error del servidor. Intenta de nuevo en unos momentos.',
  502: 'El servicio no está disponible. Intenta de nuevo en unos momentos.',
  503: 'Servicio temporalmente no disponible. Por favor, inténtalo más tarde.',
  504: 'El servidor tardó demasiado en responder. Intenta de nuevo.',
};

export { SERVER_ERROR_MESSAGES };

// NOTE: `ApiClientError` is declared ABOVE `fetchWithTimeout` (see
// AUDIT3-SHARED-011 in the comment block above the class). The duplicate
// declaration that previously lived here has been removed — keeping this
// comment as a breadcrumb so future readers don't reintroduce it.

// ─── Refresh transparente (usa cookie, no body) ─────────────────────────────
// AUDIT3-SHARED-009 (SPEC-046 QA AUDIT R1 IA — BATCH-6): the previous
// `body.data ?? body` fallback returned `body` itself when `body.data` was
// explicitly `null` (which would happen if the backend ever wrapped the
// refresh response as `{ data: null }` for a 200-with-error edge case).
// Using `body.data !== undefined ? body.data : body` makes the fallback
// fire ONLY when the `data` key is truly absent — `null` is preserved as a
// valid (if unusual) payload value.
//
// 4F-LOW-002 (SPEC-047 R3-BATCH-6): the AUDIT3-SHARED-009 fix introduced a
// regression — when `body.data === null`, the `data` variable is `null` and
// `data.tokens.accessToken` throws a TypeError (`Cannot read properties of
// null`). The try/catch caught it but surfaced as `return false` — which
// looked like an auth failure (clearing the access token + dispatching
// session-expired) instead of a parser bug. The defensive fix below uses
// `body.data ?? body` (nullish coalescing — falls back to `body` when
// `body.data` is `null` OR `undefined`) AND guards the `tokens.accessToken`
// access with optional chaining + an early-return-false. This preserves
// the dual-envelope handling (some auth endpoints return `{data:{tokens}}`,
// others `{tokens}` directly — H-02 fix) without throwing on the edge
// case where the BE sends `{data: null}`.
//
// Dual-handling: some auth endpoints return {data:{tokens}}, others return
// {tokens} directly. Makes tryRefresh resilient to future envelope changes
// (H-02 fix). If the backend is refactored to wrap /auth/refresh in the
// {data:...} envelope, this code still works.

